/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase 3.6 R2 — Share Audio (system audio publish) hook.
 *
 * Lives in the main room tab and exposes a simple start/stop API.
 * Independent of the DJ engine: when ON it publishes a SECOND LiveKit
 * audio track named `share-audio` alongside the DJ engine's master track,
 * so the DJ can keep mixing while broadcasting external system audio.
 *
 * Browser caveats (we surface them honestly to the caller via the
 * returned `lastError`):
 *   - Browsers do not expose an "audio-only" mode for getDisplayMedia.
 *     We request `{ audio: true, video: true }` and immediately discard
 *     the video track. The OS-level picker is unavoidable.
 *   - Mac Chrome/Edge: only tab audio is capturable (no system audio
 *     without a virtual driver like BlackHole). User picks a tab.
 *   - Mac Safari & Firefox: unsupported — the call rejects with
 *     NotAllowedError or NotFoundError, which we surface.
 *   - Windows Chrome/Edge: full system audio supported via the
 *     "Share system audio" checkbox at the bottom of the picker.
 *
 * Phase A R6 B8 — Chrome's tab-capture loopback behavior:
 *   When the user picks THIS tab (the Full Spectrum Center tab) for sharing, Chrome
 *   mutes the tab's local audio output to avoid feedback. The host hears
 *   the DJ deck stop on their speakers even though the LiveKit publish
 *   continues to listeners. Mitigations:
 *     1. Caller-supplied `engineRef` lets us call `engine.resume()` after
 *        getDisplayMedia in case it suspends the AudioContext.
 *     2. A keep-alive interval watches `engine.ctx.state` while sharing
 *        is on and resumes it on every transition to 'suspended'.
 *   The UX-level "pick a DIFFERENT tab" warning is surfaced by the
 *   caller (Room.jsx toast) since the picker is OS-level and we cannot
 *   modify it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { LocalAudioTrack, Track } from "livekit-client";

const TRACK_NAME = "share-audio";

export function useShareAudio({ room, enabled = true, engineRef = null }) {
  const trackRef = useRef(null);
  const streamRef = useRef(null);
  const keepAliveRef = useRef(0);
  const [on, setOn] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Phase A R6 B8 — defensive: ensure the engine's AudioContext is in
  // 'running' state. Chromium can transition it to 'suspended' after
  // getDisplayMedia even though we never explicitly suspended it.
  const resumeEngine = async () => {
    try {
      const eng = engineRef?.current;
      if (eng?.resume) await eng.resume();
      // Also force-resume the underlying ctx in case engine.resume is a no-op
      // (some test doubles short-circuit).
      if (eng?.ctx?.state === "suspended" && eng.ctx.resume) {
        await eng.ctx.resume();
      }
    } catch { /* */ }
  };

  const stop = useCallback(async () => {
    try {
      const lk = trackRef.current;
      if (lk && room) {
        try { await room.localParticipant.unpublishTrack(lk.mediaStreamTrack, true); } catch { /* */ }
      }
    } finally {
      try { trackRef.current?.stop(); } catch { /* */ }
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
      // Phase A R6 B8 — clear the keep-alive watcher when sharing stops.
      try { clearInterval(keepAliveRef.current); } catch { /* */ }
      keepAliveRef.current = 0;
      trackRef.current = null;
      streamRef.current = null;
      // Make sure the engine context is alive after we tear down the share track.
      await resumeEngine();
      setOn(false);
    }
  }, [room]);

  const start = useCallback(async () => {
    if (!enabled) { setLastError(new Error("Share Audio disabled")); return false; }
    if (!room || !room.localParticipant) {
      setLastError(new Error("Not connected to a room yet"));
      return false;
    }
    if (on) return true;
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setLastError(new Error("getDisplayMedia is not available in this browser"));
      return false;
    }
    setLastError(null);
    let stream;
    try {
      // Audio-only intent. Browsers force the screen picker; we drop the
      // video track afterwards. Some browsers refuse audio-only requests
      // outright — we then try the fallback.
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: false });
      } catch (e1) {
        stream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true });
      }
    } catch (err) {
      setLastError(err);
      setOn(false);
      return false;
    }
    // Discard any video track — we only want the audio.
    stream.getVideoTracks().forEach((t) => { try { t.stop(); } catch { /* */ } stream.removeTrack(t); });
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* */ }
      setLastError(new Error("No audio track was shared. On the picker, check 'Share system audio' (Windows) or share a tab that has audio playing (Mac)."));
      setOn(false);
      return false;
    }
    const mst = audioTracks[0];
    // Auto-stop wiring: if the user clicks the browser's "Stop sharing" button
    // the track ends — we must mirror that into our state.
    mst.addEventListener("ended", () => { stop(); }, { once: true });

    streamRef.current = stream;
    const lk = new LocalAudioTrack(mst, { name: TRACK_NAME });
    try {
      await room.localParticipant.publishTrack(lk, {
        name: TRACK_NAME,
        source: Track.Source.ScreenShareAudio,
        dtx: false,
        red: true,
        // Hint stereo where supported. Some browsers ignore this.
        audioPreset: { maxBitrate: 192000, opusStereo: true },
      });
      trackRef.current = lk;
      // Phase A R6 B8 — getDisplayMedia + tab capture can suspend the
      // engine's AudioContext on Chrome. Resume immediately so the DJ
      // deck keeps producing samples for the LiveKit publish + (where
      // Chrome doesn't suspend the tab itself) the local speakers.
      await resumeEngine();
      // Keep-alive watcher: re-resume on every transition to suspended
      // while sharing is on. Cheap (1 second polling).
      try { clearInterval(keepAliveRef.current); } catch { /* */ }
      keepAliveRef.current = setInterval(() => {
        const eng = engineRef?.current;
        if (eng?.ctx?.state === "suspended") {
          try { eng.ctx.resume?.(); } catch { /* */ }
        }
      }, 1000);
      setOn(true);
      return true;
    } catch (err) {
      setLastError(err);
      try { mst.stop(); } catch { /* */ }
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* */ }
      streamRef.current = null;
      setOn(false);
      return false;
    }
  }, [enabled, on, room, stop]);

  const toggle = useCallback(() => (on ? stop() : start()), [on, start, stop]);

  // Cleanup on unmount: stop track + unpublish so we don't leak.
  useEffect(() => {
    return () => { try { stop(); } catch { /* */ } };
  }, []);

  return { on, start, stop, toggle, lastError };
}
